import { SlicesUIElement, html } from '../element/element';
import { StringConverter } from 'html-element-property-mixins/src/utils/attribute-converters';
import '../carousel/carousel';
import '../toggle/toggle';
import '../button/button';
import '../tab-bar/tab-bar';
import { css } from './project-page.css.js';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';

/**
 * 
 * @element slicesui-project-page
 * 
 */
class SlicesUIProjectPage extends SlicesUIElement {

  static get properties() {
    return {

      tabs: {
        observe: true,
      },

      selectedTab: {
        observe: true,
        DOM: true,
        reflect: true,
        attributeName: 'selected-tab',
        fromAttributeConverter: StringConverter.fromAttribute,
        toAttributeConverter: StringConverter.toAttribute,
        changedHandler: '_selectedTabChanged'
      },

      selectedStory: {
        observe: true,
        DOM: true,
        reflect: true,
        attributeName: 'selected-story',
        fromAttributeConverter: StringConverter.fromAttribute,
        toAttributeConverter: StringConverter.toAttribute,
        changedHandler: '_selectedStoryChanged'
      },

      selectedLang: {
        observe: true,
        DOM: true,
        reflect: true,
        attributeName: 'selected-lang',
        fromAttributeConverter: StringConverter.fromAttribute,
        toAttributeConverter: StringConverter.toAttribute,
        changedHandler: '_selectedLangChanged'
      },

      canonicalUrl: {
        observe: true,
        DOM: true,
        attributeName: 'canonical-url',
        fromAttributeConverter: StringConverter.fromAttribute,
      },

      _iframeLoaded: {
        observe: true
      }
      
    };
  }

  constructor() {
    super();
    this.tabs = [];
    this.selectedStory = '';
    this.selectedLang = 'nl';
    this.canonicalUrl = 'https://player.slices.co/stories/';
  }

  connectedCallback() {
    super.connectedCallback();
    this._handleSlotMainChanged();
  }

  static get propertiesChangedHandlers() {
    return {
      render: ['tabs', 'selectedTab', 'selectedStory', 'selectedLang', 'canonicalUrl', '_iframeLoaded'],
      _setSelectedTab: ['tabs', 'selectedLang']
    };
  }

  /**
   * @private
   */
  get template() {
    return html`
      <style>${css}</style>

      <div id="languageSwitch">
        ${this.languages.length === 2 ? html`
        <span>${this.languages[0]}</span>
        <slicesui-toggle
          ?checked="${this.selectedLang === this.languages[1]}"
          @change="${this._handleSelectedLangChanged}"
        ></slicesui-toggle>
        <span>${this.languages[1]}</span>
        ` : html`
        `}
      </div>

      <section id="page">
        <header>
          <slot name="header"></slot>
        </header>
        <slicesui-carousel>
          <slot name="carousel"></slot>
        </slicesui-carousel>
        <main>
            <slicesui-tab-bar
              .selectedIndex="${this.selectedIndex}"
              @change="${this._handleTabChange}"
            >
            ${this.tabs.map(item => html`
              <slicesui-button
                text
                value="${item.id}"
                ?hidden="${item.lang !== this.selectedLang}"
                data-itemlang="${item.lang}"
              >${item.title}</slicesui-button>
            `)}
          </slicesui-tab-bar>
          <article>${unsafeHTML(this.currentTab.html)}</article>
        </main>
        <footer>
          <slot name="footer"></slot>
        </footer>
      </section>
      <section id="storyContainer" ?data-loaded="${this._iframeLoaded && this.selectedStory}">

        <slicesui-button icon id="spinner">
          <slicesui-icon icon="donut_large"></slicesui-icon>
        </slicesui-button>

        <slicesui-button icon id="close" @click="${() => this.selectedStory = ''}" ?hidden="${!this._iframeLoaded}">
          <slicesui-icon icon="close"></slicesui-icon>
        </slicesui-button>

        <iframe
          src="${this.selectedStory ? `https://player.slices.co/stories/${this.selectedStory}?canonicalUrl=${this.canonicalUrl}` : ''}"
          allowfullscreen
        ></iframe>
      </section>
      <slot name="main" @slotchange="${this._handleSlotMainChanged}" hidden></slot>
    `;
  }

  set selectedTab(tabName) {
    const tabNames = this.tabs.map(item => item.id);
    const index = tabNames.indexOf(tabName);
    if(index === -1) return this['#selectedTab'] = tabNames[0];
    this['#selectedTab'] = tabName;
  }

  get currentTab() {
    return this.tabs.find(item => item.id === this.selectedTab) || {};
  }

  get languages() {
    return Array.from(new Set(this.tabs.map(item => item.lang)));
  }

  get selectedIndex() {
    const i = this.tabs.findIndex(item => item.id == this.selectedTab);
    return (i === -1) ? 0 : i;
  }

  _handleSlotMainChanged() {
    const tabs = Array.from(this.shadowRoot.querySelector('slot[name="main"]').assignedNodes()).map(item => {
      return {
        id: item.dataset.id,
        title: item.dataset.title,
        lang: item.dataset.lang,
        html: item.innerHTML
      };
    });
    this.tabs = [...tabs];
  }

  _handleTabChange(e) {
    this.selectedTab = e.detail.value;
  }

  _selectedStoryChanged() {
    this._iframeLoaded = (this.selectedStory);
    this.dispatchEvent(new CustomEvent('selected-story-changed', {detail: {value: this.selectedStory, composed: true, bubbles: true}}));
  }

  _selectedTabChanged() {
    this.dispatchEvent(new CustomEvent('selected-tab-changed', {detail: {value: this.selectedTab, composed: true, bubbles: true}}));
  }

  _selectedLangChanged() {
    this.dispatchEvent(new CustomEvent('selected-lang-changed', {detail: {value: this.selectedLang, composed: true, bubbles: true}}));
  }

  _setSelectedTab() {
    const firstTab =  this.tabs.find(item => item.lang === this.selectedLang)
    this.selectedTab = firstTab.id;
  }

  _handleSelectedLangChanged(e) {
   this.selectedLang = (e.target.checked) ? this.languages[1] : this.languages[0];
  }

}

window.customElements.define('slicesui-project-page', SlicesUIProjectPage);